<template>
  <div>
    <!-- Controls -->
    <v-row dense class="mb-3 align-center">
      <v-col cols="auto">
        <v-btn icon @click="prevMonth">
          <v-icon>mdi-chevron-left</v-icon>
        </v-btn>
      </v-col>
      <v-col cols="auto">
        <h3>{{ monthLabel }}</h3>
      </v-col>
      <v-col cols="auto">
        <v-btn icon @click="nextMonth">
          <v-icon>mdi-chevron-right</v-icon>
        </v-btn>
      </v-col>
      <v-spacer></v-spacer>
      <v-col cols="12" sm="3">
        <v-autocomplete
          v-model="selectedUser"
          :items="users"
          item-text="full_name"
          item-value="id"
          label="Filter by User"
          clearable
          dense
          outlined
          @change="loadCalendar"
        ></v-autocomplete>
      </v-col>
    </v-row>

    <!-- Calendar Grid -->
    <v-simple-table dense>
      <thead>
        <tr>
          <th v-for="day in dayHeaders" :key="day" class="text-center">
            {{ day }}
          </th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="(week, wi) in calendarWeeks" :key="wi">
          <td
            v-for="(cell, ci) in week"
            :key="ci"
            class="text-center pa-2"
            :class="{ 'grey lighten-4': !cell.inMonth }"
            style="min-width: 100px; vertical-align: top; cursor: pointer"
            @click="cell.day ? showDayDetail(cell) : null"
          >
            <div v-if="cell.day">
              <div
                class="font-weight-bold"
                :class="{ 'primary--text': isToday(cell.date) }"
              >
                {{ cell.day }}
              </div>
              <div v-if="cell.data" class="d-flex justify-center mt-1">
                <v-chip
                  v-if="cell.data.completed + cell.data.acknowledged > 0"
                  x-small
                  color="green"
                  dark
                  class="mx-1"
                >
                  {{ cell.data.completed + cell.data.acknowledged }}
                </v-chip>
                <v-chip
                  v-if="cell.data.missed > 0"
                  x-small
                  color="red"
                  dark
                  class="mx-1"
                >
                  {{ cell.data.missed }}
                </v-chip>
                <v-chip
                  v-if="cell.data.ongoing > 0"
                  x-small
                  color="orange"
                  dark
                  class="mx-1"
                >
                  {{ cell.data.ongoing }}
                </v-chip>
                <v-chip
                  v-if="cell.data.pending > 0"
                  x-small
                  color="grey"
                  dark
                  class="mx-1"
                >
                  {{ cell.data.pending }}
                </v-chip>
              </div>
            </div>
          </td>
        </tr>
      </tbody>
    </v-simple-table>

    <!-- Legend -->
    <div class="d-flex mt-3">
      <v-chip x-small color="green" dark class="mx-1">Done</v-chip>
      <v-chip x-small color="red" dark class="mx-1">Missed</v-chip>
      <v-chip x-small color="orange" dark class="mx-1">Ongoing</v-chip>
      <v-chip x-small color="grey" dark class="mx-1">Pending</v-chip>
    </div>

    <!-- Day Detail Dialog -->
    <v-dialog v-model="dayDialog" max-width="500">
      <v-card>
        <v-card-title>{{ selectedDate }}</v-card-title>
        <v-card-text v-if="selectedDayData">
          <div>
            <strong>Total:</strong> {{ selectedDayData.total }}<br />
            <strong class="green--text">Completed/Acknowledged:</strong>
            {{ selectedDayData.completed + selectedDayData.acknowledged }}<br />
            <strong class="red--text">Missed:</strong>
            {{ selectedDayData.missed }}<br />
            <strong class="orange--text">Ongoing:</strong>
            {{ selectedDayData.ongoing || 0 }}<br />
            <strong class="grey--text">Pending:</strong>
            {{ selectedDayData.pending }}
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="dayDialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
export default {
  data() {
    return {
      currentYear: new Date().getFullYear(),
      currentMonth: new Date().getMonth(), // 0-indexed
      selectedUser: null,
      dayDialog: false,
      selectedDate: "",
      selectedDayData: null,
      dayHeaders: ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"],
    };
  },
  computed: {
    ...mapGetters({
      calendar_data: "reminder/calendar_data",
      users: "auth/users",
    }),
    monthLabel() {
      const d = new Date(this.currentYear, this.currentMonth, 1);
      return d.toLocaleString("default", { month: "long", year: "numeric" });
    },
    monthString() {
      const m = (this.currentMonth + 1).toString().padStart(2, "0");
      return `${this.currentYear}-${m}`;
    },
    calendarWeeks() {
      const firstDay = new Date(this.currentYear, this.currentMonth, 1);
      const lastDay = new Date(this.currentYear, this.currentMonth + 1, 0);
      const daysInMonth = lastDay.getDate();

      // Day of week (1=Mon, 7=Sun) for first day
      let startDow = firstDay.getDay(); // 0=Sun
      startDow = startDow === 0 ? 7 : startDow; // Convert to ISO (1=Mon)

      // Build calendar data map
      const dataMap = {};
      (this.calendar_data || []).forEach((d) => {
        dataMap[d.date] = d;
      });

      const weeks = [];
      let currentWeek = [];

      // Leading empty cells
      for (let i = 1; i < startDow; i++) {
        currentWeek.push({ day: null, inMonth: false });
      }

      for (let d = 1; d <= daysInMonth; d++) {
        const dateStr =
          this.currentYear +
          "-" +
          (this.currentMonth + 1).toString().padStart(2, "0") +
          "-" +
          d.toString().padStart(2, "0");
        currentWeek.push({
          day: d,
          date: dateStr,
          inMonth: true,
          data: dataMap[dateStr] || null,
        });
        if (currentWeek.length === 7) {
          weeks.push(currentWeek);
          currentWeek = [];
        }
      }

      // Trailing empty cells
      while (currentWeek.length > 0 && currentWeek.length < 7) {
        currentWeek.push({ day: null, inMonth: false });
      }
      if (currentWeek.length) weeks.push(currentWeek);

      return weeks;
    },
  },
  created() {
    if (!this.users || this.users.length === 0) {
      this.get_users();
    }
    this.loadCalendar();
  },
  methods: {
    ...mapActions({
      get_calendar_data: "reminder/get_calendar_data",
      get_users: "auth/get_users",
    }),
    loadCalendar() {
      this.get_calendar_data({
        month: this.monthString,
        user_id: this.selectedUser,
      });
    },
    prevMonth() {
      if (this.currentMonth === 0) {
        this.currentMonth = 11;
        this.currentYear--;
      } else {
        this.currentMonth--;
      }
      this.loadCalendar();
    },
    nextMonth() {
      if (this.currentMonth === 11) {
        this.currentMonth = 0;
        this.currentYear++;
      } else {
        this.currentMonth++;
      }
      this.loadCalendar();
    },
    isToday(dateStr) {
      return dateStr === new Date().toISOString().substr(0, 10);
    },
    showDayDetail(cell) {
      if (cell.data) {
        this.selectedDate = cell.date;
        this.selectedDayData = cell.data;
        this.dayDialog = true;
      }
    },
  },
};
</script>
